/**
 * DailyViralInspiracje — Organic → Inspiracje.
 * Zero-config feed dziennych viralek pod niszę usera + one-tap brief do Reel Creatora.
 *
 * Flow:
 *  1) brak profilu → onboarding (nisza + rynki + opcjonalny @handle) → zapis profilu
 *  2) profil jest → feed viralek (sort po viralScore) z filtrami + "Zrób reelsa"
 *
 * Feed czytany z globalnego cache (viral_inspirations). Zasilanie cache (ingest
 * ScrapeCreators/Apify) to osobny worker — do czasu jego wdrożenia feed bywa pusty
 * (obsłużone przez EmptyState).
 */
import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Flame, Loader2, ExternalLink, Wand2, Settings2, Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useStore } from "@/lib/store";
import {
  NICHES,
  MARKETS,
  formatViralScore,
  nicheLabel,
  marketLabel,
} from "@shared/daily-viral";
import type { ViralInspiration, UserContentProfile } from "@shared/schema";
import {
  useDailyViralProfile,
  useDailyViralFeed,
  useSaveDailyViralProfile,
  type FeedFilters,
} from "@/hooks/useDailyViral";

const numberFmt = new Intl.NumberFormat("pl-PL");

function OnboardingCard({ existing }: { existing: UserContentProfile | null }) {
  const { toast } = useToast();
  const save = useSaveDailyViralProfile();
  const [niche, setNiche] = useState<string>(existing?.niche ?? "");
  const [markets, setMarkets] = useState<string[]>(existing?.markets ?? ["US"]);
  const [handle, setHandle] = useState<string>(existing?.sourceHandle ?? "");

  const toggleMarket = (key: string) =>
    setMarkets((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]
    );

  const onSave = () => {
    if (!niche || markets.length === 0) return;
    save.mutate(
      {
        niche,
        markets,
        sourceHandle: handle.trim() || undefined,
        sourcePlatform: handle.includes("tiktok") ? "tiktok" : undefined,
      },
      {
        onSuccess: () => toast({ title: "Profil zapisany", description: "Ładuję Twoje inspiracje…" }),
        onError: () => toast({ title: "Nie udało się zapisać", variant: "destructive" }),
      }
    );
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-500" />
          {existing ? "Edytuj profil inspiracji" : "Ustaw swój feed inspiracji"}
        </CardTitle>
        <CardDescription>
          Wybierz niszę i rynki — codziennie podrzucimy Ci viralowe filmy zza granicy, gotowe do
          przerobienia na Twojego reelsa.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Twoja nisza</Label>
          <Select value={niche} onValueChange={setNiche}>
            <SelectTrigger>
              <SelectValue placeholder="Wybierz niszę…" />
            </SelectTrigger>
            <SelectContent>
              {NICHES.map((n) => (
                <SelectItem key={n.key} value={n.key}>
                  {n.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Rynki inspiracji</Label>
          <div className="flex flex-wrap gap-2">
            {MARKETS.map((m) => (
              <Badge
                key={m.key}
                variant={markets.includes(m.key) ? "default" : "outline"}
                className="cursor-pointer select-none"
                onClick={() => toggleMarket(m.key)}
              >
                {m.label}
              </Badge>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>
            Twój Instagram / TikTok <span className="text-muted-foreground">(opcjonalnie)</span>
          </Label>
          <Input
            placeholder="@twojhandle"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
          />
        </div>

        <Button onClick={onSave} disabled={!niche || markets.length === 0 || save.isPending}>
          {save.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {existing ? "Zapisz zmiany" : "Pokaż moje inspiracje"}
        </Button>
      </CardContent>
    </Card>
  );
}

function ViralScoreBadge({ score }: { score: number | null }) {
  if (score == null) return null;
  return (
    <Badge className="bg-orange-500/90 text-white border-0 gap-1">
      <Flame className="w-3 h-3" />
      {formatViralScore(score)}
    </Badge>
  );
}

function ViralCard({ item, onMakeReel }: { item: ViralInspiration; onMakeReel: (i: ViralInspiration) => void }) {
  return (
    <Card className="overflow-hidden flex flex-col">
      <div className="relative aspect-[9/16] bg-muted">
        {item.thumbnailUrl ? (
          <img
            src={item.thumbnailUrl}
            alt={item.title ?? "Miniatura viralowego filmu"}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Flame className="w-8 h-8" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1">
          <ViralScoreBadge score={item.viralScore} />
        </div>
        <Badge variant="secondary" className="absolute top-2 right-2 capitalize">
          {item.platform}
        </Badge>
      </div>
      <CardContent className="p-3 flex flex-col gap-2 flex-1">
        {item.hook && <p className="text-sm font-medium line-clamp-3">{item.hook}</p>}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {item.authorHandle && <span className="truncate">@{item.authorHandle}</span>}
          {item.viewCount != null && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {numberFmt.format(item.viewCount)}
            </span>
          )}
        </div>
        <div className="flex gap-2 mt-auto pt-1">
          <Button size="sm" className="flex-1" onClick={() => onMakeReel(item)}>
            <Wand2 className="w-4 h-4 mr-1" />
            Zrób reelsa
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href={item.sourceUrl} target="_blank" rel="noreferrer" aria-label="Otwórz źródło">
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyFeed({ market }: { market: string | null }) {
  return (
    <Card className="text-center py-12">
      <CardContent className="flex flex-col items-center gap-3">
        <Flame className="w-10 h-10 text-orange-500/60" />
        <p className="font-medium">Brak viralek w tej niszy{market ? ` (${marketLabel(market)})` : ""} — na razie</p>
        <p className="text-sm text-muted-foreground max-w-md">
          Codzienny import viralowych filmów jest w drodze. Gdy tylko zasilimy feed, znajdziesz tu
          najlepsze formaty zza granicy pod Twoją niszę.
        </p>
      </CardContent>
    </Card>
  );
}

export default function DailyViralInspiracje() {
  const { toast } = useToast();
  const setCurrentModule = useStore((s) => s.setCurrentModule);
  const profileQuery = useDailyViralProfile();
  const [editing, setEditing] = useState(false);
  const [filters, setFilters] = useState<FeedFilters>({});
  const feedQuery = useDailyViralFeed(filters);

  const profile = profileQuery.data ?? null;
  const activeMarket = filters.market ?? profile?.markets?.[0] ?? null;

  const marketOptions = useMemo(
    () => (profile?.markets ?? MARKETS.map((m) => m.key)),
    [profile?.markets]
  );

  const onMakeReel = (item: ViralInspiration) => {
    const brief = [
      `Zrób reelsa na bazie tego viralowego formatu (${formatViralScore(item.viralScore)} outlier).`,
      item.hook ? `Hook: ${item.hook}` : "",
      item.transcript ? `Transkrypt oryginału:\n${item.transcript}` : "",
      profile?.offer ? `Dostosuj pod moją ofertę: ${profile.offer}` : "",
      `Źródło: ${item.sourceUrl}`,
    ]
      .filter(Boolean)
      .join("\n\n");
    void navigator.clipboard?.writeText(brief);
    setCurrentModule("reel-creator");
    toast({
      title: "Brief skopiowany",
      description: "Otwieram Reel Creator — wklej brief i generuj scenariusz.",
    });
  };

  if (profileQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile || editing) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <OnboardingCard existing={editing ? profile : null} />
      </div>
    );
  }

  const items = feedQuery.data?.items ?? [];

  return (
    <div className="flex-1 overflow-auto p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            Daily Viral — {nicheLabel(profile.niche)}
          </h2>
          <p className="text-sm text-muted-foreground">Najlepsze formaty zza granicy pod Twoją niszę</p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={filters.market ?? "__all"}
            onValueChange={(v) => setFilters((f) => ({ ...f, market: v === "__all" ? undefined : v }))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Rynek" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Wszystkie rynki</SelectItem>
              {marketOptions.map((m) => (
                <SelectItem key={m} value={m}>
                  {marketLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Settings2 className="w-4 h-4 mr-1" />
            Profil
          </Button>
        </div>
      </div>

      {feedQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <EmptyFeed market={activeMarket} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((item) => (
            <ViralCard key={item.id} item={item} onMakeReel={onMakeReel} />
          ))}
        </div>
      )}
    </div>
  );
}
